/**
 * Configuration for the Agent Builder API.
 *
 * Both the Agent Builder API URL and the Chat Assistant Agent ID are
 * fetched from the Credit Control backend via `GET /api/v1/agent/config`
 * and cached in memory.
 *
 * The instance ID for streaming requests uses the same instance ID
 * that the frontend already has from its auth provider.
 *
 * Call {@link initAgentConfig} early in the application lifecycle so the
 * synchronous getters have a populated cache to read from.
 */

import {
  fetchAgentConfig,
  getCachedAgentConfig,
} from '@/services/agent-config.service';

/**
 * Fetch the Agent Builder configuration from the backend and cache it.
 *
 * Should be called once during application startup (e.g. in an effect or
 * initialisation routine).  Subsequent calls are cheap — the cached
 * promise / result is returned immediately.
 */
export async function initAgentConfig(): Promise<void> {
  try {
    await fetchAgentConfig();
  } catch {
    // Non-fatal — the panel will retry when opened.
  }
}

export function getAgentBuilderApiUrl(): string {
  const cached = getCachedAgentConfig();
  if (cached?.agentBuilderBaseUrl) {
    const url = cached.agentBuilderBaseUrl;
    return url.endsWith('/') ? url.slice(0, -1) : url;
  }

  throw new Error(
    'Agent Builder API URL is not configured. ' +
    'Ensure initAgentConfig() has been called and the backend AgentApi config is set.',
  );
}

export function getChatAssistantAgentId(): string {
  const cached = getCachedAgentConfig();
  if (cached?.chatAssistantAgentId) {
    return cached.chatAssistantAgentId;
  }

  throw new Error(
    'Chat Assistant Agent ID is not configured. ' +
    'Ensure initAgentConfig() has been called and the backend AgentApi config is set.',
  );
}
